import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Unstable_Grid2";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { theme } from "@answersnow/pkg-theme";
import { TABLE_HEADER_CELL_SX, TABLE_BODY_CELL_SX } from "./constants";
import { TaskRow } from "./TaskRow";
import type { Task, TaskType } from "./types";

const SKELETON_ROW_COUNT = 5;

interface TasksTableProps {
  tasks: Task[];
  selectedTab: TaskType;
  loading?: boolean;
  onTaskSelect?: (task: Task) => void;
}

export const TasksTable: React.FC<TasksTableProps> = ({
  tasks,
  selectedTab,
  loading,
  onTaskSelect,
}) => {
  const renderTableBody = () => {
    if (loading) {
      return Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
        <TableRow
          key={`skeleton-${selectedTab}-${index}`}
          sx={{
            borderBottom:
              index < SKELETON_ROW_COUNT - 1 ? "1px solid" : "0px solid transparent",
            borderColor: theme.palette.divider,
          }}
        >
          <TableCell sx={TABLE_BODY_CELL_SX}>
            <Skeleton variant="text" width="60%" />
          </TableCell>
          <TableCell sx={TABLE_BODY_CELL_SX}>
            <Skeleton variant="text" width="40%" />
          </TableCell>
        </TableRow>
      ));
    }

    if (tasks.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={2} align="center" sx={{ p: 6 }}>
            <Typography variant="body2" color="text.secondary">
              No {selectedTab} tasks
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return tasks.map((task, index) => (
      <TaskRow
        key={task.id}
        task={task}
        isLast={index === tasks.length - 1}
        onSelect={onTaskSelect}
      />
    ));
  };

  return (
    <Grid xs={12}>
      <Box
        sx={{
          borderRadius: "16px",
          bgcolor: theme.palette.background.paper,
          border: "1px solid",
          borderColor: theme.palette.divider,
          overflow: "hidden",
        }}
        data-testid="tasks-card"
      >
        <Table
          aria-label={`${selectedTab} tasks table`}
          data-testid="tasks-table"
          sx={{ borderCollapse: "collapse" }}
        >
          <TableHead>
            <TableRow
              sx={{
                borderBottom: "1px solid",
                borderColor: theme.palette.divider,
              }}
            >
              <TableCell sx={TABLE_HEADER_CELL_SX}>Task</TableCell>
              <TableCell sx={TABLE_HEADER_CELL_SX}>
                {selectedTab === "past" ? "Completion Date" : "Administration Date"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </Box>
    </Grid>
  );
};
